import { AbstractControl } from '@angular/forms';
import validator from 'validator';
import isPostalCode from 'validator/lib/isPostalCode';

export class CustomValidators {

  /**
   * sample from http://blog.thoughtram.io/angular/2016/03/14/custom-validators-in-angular-2.html
   */
  static validateEmail(c: AbstractControl<string>) {
    
    if (!c.value || c.value === '') {
      return null;
    }

    return validator.isEmail(c.value) ?
      null : {
        validateEmail: {
          valid: false
        }
      };
  }

  // ^^ needs to change when this validator changes
  // empty string returns false 
  static validateEmailString( email: string ): boolean{
	if ( !email )
		return false;
		return validator.isEmail(email)
  }

  static validateMobile(c: AbstractControl<string>) {
    const E164_REGEXP = /^\+{1}[1-9]\d{1,14}$/g;

    if (!c.value || c.value === '') {
      return null;
    }

    return E164_REGEXP.test(c.value) ? null : {
      validateMobile: {
        valid: false
      }
    };
  }

	static validateZipCode(value: string, countryCode: string): boolean {
		const threeDigits = /^\d{3}$/;
		const fourDigits = /^\d{4}$/;
		const fiveDigits = /^\d{5}$/;
		const sixDigits = /^\d{6}$/; 
		const zipCodes = {
			'SA': /(^\d{5}-{1}\d{4}$)|(^\d{5}$)/, // NNNNNN or NNNNN-NNNN
			'AR': /^([a-zA-Z]{1}\d{4}[a-zA-Z]{3}|[a-zA-Z]{1}\d{4}|\d{4})$/, // NNNN or LNNNN or LNNNNLLL
			'LB': /(^\d{5}$)|(^\d{4}\s{0,1}\d{4}$)/, // NNNN NNNN or NNNNNNNN or NNNNN
			'CL': /^(\d{7})|[0-9]{3}[-][0-9]{4}$/, // NNNNNNN (NNN-NNNN)
			'MU': /^([1-9]|A|R)\d{4,4}$/i, // NNNNN or ANNNN or RNNNN
			'TF': fiveDigits,
			'MG': threeDigits,
			'CV': fourDigits,
			'CY': fourDigits,
			'SV': fourDigits,
			'EG': fiveDigits,
			'JO': fiveDigits,
			'RS': fiveDigits,
			'CO': sixDigits,
			'SG': sixDigits,
			'GP': /^97[01]\d{2}$/,
			'MQ': /^972\d{2}$/ 
		};
		let	result: boolean;
		if ( zipCodes[countryCode]) {
			result = (new RegExp(zipCodes[countryCode])).test(value);
		} else {
			result = isPostalCode(value, countryCode as any);
		}
		return result;
	}

	static noWhitespaceValidator(c: AbstractControl<string>) {
		if (!c.value || c.value === '' || c.value.trim() === '') {
			return {'required': true};
		}
		return null;
	}

  static validateName(c: AbstractControl<string>) {
    const regex = new RegExp(/((^ +$)|[\[\]\|\\\/])+/gu);
		if (!c.value || c.value === '' || c.value.trim() === '') {
			return null;
		}
		if (regex.test(c.value)) {
			return {'pattern': true};
		}
		return null;
	}

	//^^ must be the same validation with different return values
	// return false if empty
	static validateNameString( name: string ): boolean {
        const regex = new RegExp(/((^ +$)|[\[\]\|\\\/])+/gu);
		if (!name || name === '')
			return false;

		return !regex.test(name);
	}

	//^^ must be the same validation with different return values
	// return false if empty
	static validateSearchByString( value: string ): boolean
	{
		if ( !value )
			return false;

		const regex = new RegExp(/^[a-zA-Z0-9-._+@]*$/g);
		if (regex.test(value)) {
			return true;
		}
		return false;
	}

	static validateAccountNameFormat(c: AbstractControl<string>) {
		// eventually we may want to add more restrictions to the Customer Name format - like we do for a users name
		// e.g special characters - see custom validator called validateName
		if(c.value)
		{
			if(c.value.length > 0 && c.value.trim().length === 0){	//there is a name but it's just spaces
				return {'invalidFormat': true};
			}
		}
		return null;
	}

	static userHasValidEmail(user) {
		if (user ) {
		  if (user.source === 'APP') {
			return true;
		  } else {
			return this.validateEmailString(user.email);
		  }
		} else {
		  return false;
		}
  }

  static validateFeatureFlagId(c: AbstractControl<string>) {
    const regex = new RegExp(/^[a-zA-Z0-9-]*$/g);
    if (!c.value || c.value === '' || c.value.trim() === '') {
      return null;
    }
    return regex.test(c.value) ? null : { 'pattern': true };
  }

  static validateFeatureFlagName(c: AbstractControl<string>) {
    const regex = new RegExp(/^[a-zA-Z0-9-_]*$/g);
    if (!c.value || c.value === '' || c.value.trim() === '') {
      return null;
    }
    return regex.test(c.value) ? null : { 'pattern': true };
  }

  static validateAccountId(c: AbstractControl<string>) {
    const regex = new RegExp(/^[a-zA-Z0-9-_]*$/g);
    if (!c.value || c.value === '' || c.value.trim() === '') {
      return null;
    }
    return regex.test(c.value) ? null : { 'pattern': true };
  }

  static validatePortNumber(c: AbstractControl<string>) {
    const regex = new RegExp(/^([1-9][0-9]{0,3}|[1-5][0-9]{4}|6[0-4][0-9]{3}|65[0-4][0-9]{2}|655[0-2][0-9]|6553[0-5])$/g);
    if (!c.value || c.value === '' || c.value.trim() === '') {
      return null;
    }
    return regex.test(c.value) ? null : { 'pattern': true };
  }

  // taken from GWP app - v2.3.0
  // added (\/((?:[1-9])|(?:[1-2][0-9])|(?:3[0-1])))? for subnet validation
  static validateDottecDecimalWithSubnet(value: string): boolean {
    // tslint:disable-next-line:max-line-length
    const IP_REGEXP = /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)(\/((?:[1-9])|(?:[1-2][0-9])|(?:3[0-2])))?$/;
    const disallowed_IP = ['0.0.0.0', '127.0.0.1', '255.255.255.255'];
    return IP_REGEXP.test(value) && disallowed_IP.indexOf(value) === -1;
  }

  //https://stackoverflow.com/questions/16463666/javascript-regex-to-match-fully-qualified-domain-name-without-protocol-optiona
  static validateFQDN(value: string): boolean {
    const FQDN_REGEXP = /^(?=^.{1,254}$)(^(?:(?!\d+\.|-)[a-zA-Z0-9_\-]{1,63}(?<!-)\.)+(?:[a-zA-Z]{2,})$)$/;
    return FQDN_REGEXP.test(value);
  }

  static validateIpAddressOrFQDN(c: AbstractControl<string>) {
    if (!c.value) {
      return null;
    }
    return CustomValidators.validateDottecDecimalWithSubnet(c.value) || CustomValidators.validateFQDN(c.value) ?
      null : { 'invalid': true };
  }
}
